/**
 * Tiny clipboard helper for the DVS EFT bank-transfer IBAN copy button.
 * Uses a single delegated document listener so it works for any number of
 * instances on the page (product page card + cart drawer card can both be
 * present at once) without duplicate listener registration, and keeps
 * working after Section Rendering API DOM morphs with no re-binding needed.
 */

/**
 * Copies text to the clipboard, falling back to a hidden textarea +
 * execCommand for contexts where the async Clipboard API is unavailable
 * (e.g. insecure origins or older browsers).
 * @param {string} text
 * @returns {Promise<void>}
 */
async function copyText(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to the legacy fallback below.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    textarea.remove();
  }
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const button = event.target.closest('[data-dvs-copy-iban]');
  if (!(button instanceof HTMLButtonElement)) return;

  const iban = button.dataset.iban ?? '';
  const defaultLabel = button.dataset.defaultLabel ?? 'Kopyala';
  const copiedLabel = button.dataset.copiedLabel ?? defaultLabel;

  copyText(iban)
    .then(() => {
      const existingTimeout = button.dataset.dvsCopyTimeout;
      if (existingTimeout) window.clearTimeout(Number(existingTimeout));

      button.textContent = copiedLabel;
      const timeoutId = window.setTimeout(() => {
        button.textContent = defaultLabel;
        delete button.dataset.dvsCopyTimeout;
      }, 2000);
      button.dataset.dvsCopyTimeout = String(timeoutId);
    })
    .catch((error) => {
      console.warn('[dvs-eft-bank-copy] Copy failed:', error);
    });
});
